import type { Rgb } from '../color';
export type MockupView = 'front' | 'back';
export type MockupKind = 'flat' | 'model';
export type Point = [number, number];
export interface Quad { tl: Point; tr: Point; br: Point; bl: Point }
/** One printable region on a blank: where it sits on the photo (`quad`) and how big it is in inches.
 *  `id` is a slug unique within its template — the handle a job's extra prints name an area by.
 *  `id`/`name` are optional ONLY so that pre-3C-1 callers, which knew one unnamed area, keep
 *  compiling: everything `parseTemplate` or `prepareTemplate` returns carries both. */
export interface PrintArea { id?: string; name?: string; quad: Quad; widthIn: number; heightIn: number }
/** The areas a shop asks for by name, with the sizes a Gildan-sized blank takes. `parseTemplate`
 *  fills a missing name/size on an area whose id is one of these. */
export const AREA_PRESETS: Record<'chest' | 'back' | 'sleeve' | 'pocket', { name: string; widthIn: number; heightIn: number }> = {
  chest: { name: 'Chest', widthIn: 12, heightIn: 16 },
  back: { name: 'Back', widthIn: 12, heightIn: 16 },
  sleeve: { name: 'Sleeve', widthIn: 4, heightIn: 4 },
  pocket: { name: 'Pocket', widthIn: 4, heightIn: 4 },
};
/** More than this and the area picker stops being a picker; it is also the ceiling the compositor's
 *  per-print loop is budgeted against. */
export const MAX_PRINT_AREAS = 8;
export interface MaskSettings { method: 'background' | 'garment'; tolerance: number; bgTolerance: number; featherPx: number; closePx: number }
export interface ShadingSettings { blurPx: number | 'auto'; foldBlurPx?: number | 'auto'; wrinkle: number; texture: number; lmid: number | 'auto' }
export interface MockupTemplate {
  version: 1; id: string; name: string; style: string; view: MockupView; kind: MockupKind; colorName: string;
  photo: { width: number; height: number; src: string };
  garmentColor: Rgb;
  /** Every printable area on this blank, in the order they are offered. Always 1–8 entries with
   *  unique ids once it has been through `parseTemplate` — optional only so a pre-3C-1 template
   *  object, which carried `printArea` alone, still satisfies the type. Read it through `areasOf`. */
  printAreas?: PrintArea[];
  /** The primary area — always `printAreas[0]`, by identity, on anything `parseTemplate` returned.
   *  Kept as a field of its own so every pre-3C caller (and the whole web UI) keeps working. */
  printArea: PrintArea;
  mask: MaskSettings; shading: ShadingSettings;
  /** False when the garment mask couldn't be separated from the backdrop by colour (see `autoMask`'s
   *  `separable` flag) — the UI should not offer garment recolouring for such a template. Defaults to
   *  true when omitted. */
  recolorable?: boolean;
}
export interface PreparedMaps {
  width: number; height: number; mask: Uint8Array; lumaBlur: Float32Array; gx: Int8Array; gy: Int8Array; residual: Int8Array; lmid: number;
  /** The garment's drop shadow, 0–255 (see `shadowMap`). Present iff the mask was separable — a
   *  template whose garment can't be told from its backdrop has no trustworthy silhouette to cast
   *  one, and its absence is what makes `renderMockup` ignore a job's backdrop. */
  shadow?: Uint8Array;
}
/** What a job puts behind the garment instead of the photo's own studio sweep. A scene names a user
 *  image by id (the web's `eip-scenes` store); the caller resolves it to a photo-sized `RgbaImage`
 *  (`ResolvedBackdrop`) before rendering. */
export type Backdrop = { kind: 'color'; rgb: Rgb } | { kind: 'gradient'; top: Rgb; bottom: Rgb } | { kind: 'scene'; sceneId: string };
export type InkLook = 'screen' | 'dtf';
export interface LookParams { shadowDepth: number; highlight: number; texture: number; opacity: number }
export const LOOKS: Record<InkLook, LookParams> = {
  screen: { shadowDepth: 1.0, highlight: 0.5, texture: 0.35, opacity: 0.92 },
  dtf: { shadowDepth: 0.6, highlight: 0.35, texture: 0.12, opacity: 1.0 },
};
/** Where a print sits in its area, in inches. `'center'` on an axis is a STICKY centre — the
 *  midline of the area, recomputed from the placed size, so it survives a width change — which is
 *  what the panel's "Centre across" / "Centre down" toggles write. */
export interface Placement { widthIn: number; xIn: number | 'center'; yIn: number | 'center' }
/** An extra print beyond the job's primary one: which area it goes in, which open document's art it
 *  uses (`null` = this job's own art), and how it sits there. */
export interface MockupPrint { areaId: string; docId: string | null; placement: Placement; rotationDeg: number; enabled: boolean }
export interface MockupJob {
  templateId: string; look: InkLook; placement: Placement;
  /** Degrees clockwise about the art rect's centre, −180…180. */
  rotationDeg: number;
  /** Prints in the template's other areas, composited over the primary in order. */
  extraPrints: MockupPrint[];
  garment: Rgb | null; wrinkle: number | null; texture: number | null;
  /** What replaces the photo's backdrop, or null/absent for the photo's own. Optional so a pre-3C-3
   *  job object still satisfies the type; read it as `job.backdrop ?? null`. Nothing in the engine
   *  reads this field — the caller resolves it and hands `renderMockup` a `ResolvedBackdrop`. */
  backdrop?: Backdrop | null;
}
export const DEFAULT_PRINT_AREA_IN = { widthIn: 12, heightIn: 16 } as const;
export const PHOTO_MAX_SIDE = 3000;
export const PHOTO_MIN_SIDE = 600;

export function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }
export function clamp01(v: number): number { return clamp(v, 0, 1); }
export function defaultMaskSettings(kind: MockupKind): MaskSettings {
  return kind === 'flat' ? { method: 'background', tolerance: 0.08, bgTolerance: 0.08, featherPx: 2, closePx: 4 } : { method: 'garment', tolerance: 0.2, bgTolerance: 0.04, featherPx: 2, closePx: 4 };
}
export function defaultShadingSettings(): ShadingSettings { return { blurPx: 'auto', foldBlurPx: 'auto', wrinkle: 0.006, texture: 0.2, lmid: 'auto' }; }
export function slugify(s: string): string { return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
let idCounter = 0;
export function newTemplateId(prefix = 't'): string { idCounter += 1; return `${prefix}-${Date.now().toString(36)}-${idCounter}`; }

function cross(o: Point, a: Point, b: Point): number { return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]); }
/** True when the four corners (clockwise from top-left) form a strictly convex, non-degenerate quadrilateral. */
export function isConvexQuad(q: Quad): boolean {
  const p = [q.tl, q.tr, q.br, q.bl];
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const c = cross(p[i], p[(i + 1) % 4], p[(i + 2) % 4]);
    if (Math.abs(c) < 1e-6) return false;
    const s = Math.sign(c);
    if (sign === 0) sign = s; else if (s !== sign) return false;
  }
  return true;
}
export function quadInside(q: Quad, width: number, height: number): boolean {
  return [q.tl, q.tr, q.br, q.bl].every(([x, y]) => x >= 0 && y >= 0 && x <= width && y <= height);
}
const num = (v: unknown, lo: number, hi: number, def: number): number => (typeof v === 'number' && Number.isFinite(v) ? clamp(v, lo, hi) : def);
const point = (v: unknown, name: string): Point => {
  if (!Array.isArray(v) || v.length !== 2 || !v.every((n) => typeof n === 'number' && Number.isFinite(n))) throw new Error(`template: ${name} is not a point`);
  return [v[0], v[1]];
};
/** The areas of a template, tolerating a pre-3C one that only ever had `printArea`. */
export function areasOf(t: { printAreas?: PrintArea[]; printArea: PrintArea }): PrintArea[] {
  return t.printAreas && t.printAreas.length ? t.printAreas : [t.printArea];
}
export function areaById(t: { printAreas?: PrintArea[]; printArea: PrintArea }, id: string): PrintArea | undefined {
  return areasOf(t).find((a) => a.id === id);
}
/** `left-sleeve` → `Left Sleeve`: the shop-facing name of an area nobody named, built from the
 *  slug itself. Ids are lower-case alphanumerics and hyphens by the time this sees one. */
export function titleCaseId(id: string): string {
  return id.split('-').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
}
/** The preset behind an id, if it is one of the four the picker offers. */
const presetFor = (id: string) => (AREA_PRESETS as Record<string, { name: string; widthIn: number; heightIn: number }>)[id];
/** The name an area with none falls back on: its preset's, else its own id title-cased. Never the
 *  VIEW's name — that belongs to the migrated `printArea` alone, and calling a hand-authored
 *  `left-sleeve` area "Chest" misnames where the ink goes. */
const nameFor = (id: string) => presetFor(id)?.name ?? titleCaseId(id);
/**
 * Every area with an `id` and a `name` on it, filling in what a hand-built one (the editor's bare
 * `{ quad, widthIn, heightIn }`) lacks: `firstId` for the primary, `area-N` after that. An area
 * that already carries both is returned UNCHANGED — same object — so nothing that compares by
 * identity (`printArea === printAreas[0]`) churns on a template that has been through
 * `parseTemplate`.
 */
export function withAreaIdentity(areas: PrintArea[], firstId = 'chest'): PrintArea[] {
  return areas.map((a, i) => {
    if (a.id && a.name) return a;
    const id = a.id || (i === 0 ? firstId : `area-${i + 1}`);
    return { ...a, id, name: a.name || nameFor(id) };
  });
}
function parseArea(raw: unknown, width: number, height: number, fallbackId: string, seen: Set<string>): PrintArea {
  const r = (raw ?? {}) as Record<string, any>;
  const q = r.quad ?? {};
  const quad: Quad = { tl: point(q.tl, 'quad.tl'), tr: point(q.tr, 'quad.tr'), br: point(q.br, 'quad.br'), bl: point(q.bl, 'quad.bl') };
  if (!isConvexQuad(quad)) throw new Error('template: print area must be a convex quad');
  if (!quadInside(quad, width, height)) throw new Error('template: print area lies outside the photo');
  const id = r.id === undefined ? fallbackId : slugify(String(r.id));
  if (!id) throw new Error('template: a print area needs an id');
  if (seen.has(id)) throw new Error(`template: duplicate print area id "${id}"`);
  seen.add(id);
  const preset = presetFor(id);
  const name = typeof r.name === 'string' && r.name.trim() ? r.name.trim() : nameFor(id);
  return { id, name, quad, widthIn: num(r.widthIn, 1, 40, preset?.widthIn ?? DEFAULT_PRINT_AREA_IN.widthIn), heightIn: num(r.heightIn, 1, 40, preset?.heightIn ?? DEFAULT_PRINT_AREA_IN.heightIn) };
}
/**
 * A template as JSON, without the `printArea` alias (it is `printAreas[0]`, and writing both is how
 * the two drift apart) and without any undefined field. This is what the authoring script and the
 * web's template store write; `parseTemplate` reads it straight back.
 */
export function serializeTemplate(t: MockupTemplate): Record<string, unknown> {
  const areas = areasOf(t);
  const s: Record<string, unknown> = {
    version: 1, id: t.id, name: t.name, style: t.style, view: t.view, kind: t.kind, colorName: t.colorName,
    photo: { width: t.photo.width, height: t.photo.height, src: t.photo.src },
    garmentColor: [t.garmentColor[0], t.garmentColor[1], t.garmentColor[2]],
    // A hand-built area (never through parseTemplate) may carry neither id nor name — fill the same
    // ones the migration would, so nothing is ever written as undefined.
    printAreas: withAreaIdentity(areas, t.view === 'back' ? 'back' : 'chest').map((a) => ({
      id: a.id, name: a.name,
      quad: { tl: [a.quad.tl[0], a.quad.tl[1]], tr: [a.quad.tr[0], a.quad.tr[1]], br: [a.quad.br[0], a.quad.br[1]], bl: [a.quad.bl[0], a.quad.bl[1]] },
      widthIn: a.widthIn, heightIn: a.heightIn,
    })),
    mask: { method: t.mask.method, tolerance: t.mask.tolerance, bgTolerance: t.mask.bgTolerance, featherPx: t.mask.featherPx, closePx: t.mask.closePx },
    shading: { blurPx: t.shading.blurPx, ...(t.shading.foldBlurPx === undefined ? {} : { foldBlurPx: t.shading.foldBlurPx }), wrinkle: t.shading.wrinkle, texture: t.shading.texture, lmid: t.shading.lmid },
  };
  if (t.recolorable !== undefined) s.recolorable = t.recolorable;
  return s;
}
/** What `parseTemplate` hands back: a template whose `printAreas` is there, validated (1–8, unique
 *  slug ids) and whose `printArea` IS `printAreas[0]`. */
export type ParsedTemplate = MockupTemplate & { printAreas: PrintArea[] };
export function parseTemplate(raw: unknown): ParsedTemplate {
  const r = (raw ?? {}) as Record<string, any>;
  if (r.version !== 1) throw new Error('template: unsupported version');
  for (const k of ['id', 'name', 'style', 'colorName']) if (typeof r[k] !== 'string' || !r[k]) throw new Error(`template: missing ${k}`);
  const view: MockupView = r.view === 'back' ? 'back' : 'front';
  const kind: MockupKind = r.kind === 'model' ? 'model' : 'flat';
  const photo = r.photo ?? {};
  const width = num(photo.width, 1, 1e5, 0), height = num(photo.height, 1, 1e5, 0);
  if (!width || !height || typeof photo.src !== 'string') throw new Error('template: photo needs width, height and src');
  // `printAreas` is authoritative when the key is there at all — a template carrying both is one the
  // store has already migrated, and the alias is the stale copy. Everything older has one area,
  // named after the view so the id means something to the picker.
  const legacyId = view === 'back' ? 'back' : 'chest';
  const seen = new Set<string>();
  let printAreas: PrintArea[];
  if (Array.isArray(r.printAreas)) {
    if (r.printAreas.length < 1 || r.printAreas.length > MAX_PRINT_AREAS) throw new Error(`template: a template needs 1–${MAX_PRINT_AREAS} print areas`);
    printAreas = r.printAreas.map((a: unknown, i: number) => parseArea(a, width, height, i === 0 ? legacyId : `area-${i + 1}`, seen));
  } else {
    printAreas = [parseArea(r.printArea, width, height, legacyId, seen)];
  }
  const md = defaultMaskSettings(kind), m = r.mask ?? {};
  const sd = defaultShadingSettings(), s = r.shading ?? {};
  const g = Array.isArray(r.garmentColor) ? r.garmentColor : [128, 128, 128];
  return {
    version: 1, id: r.id, name: r.name, style: r.style, view, kind, colorName: r.colorName,
    photo: { width, height, src: photo.src },
    garmentColor: [num(g[0], 0, 255, 128), num(g[1], 0, 255, 128), num(g[2], 0, 255, 128)],
    recolorable: r.recolorable === false ? false : true,
    printAreas, printArea: printAreas[0],
    mask: { method: m.method === 'garment' ? 'garment' : m.method === 'background' ? 'background' : md.method, tolerance: num(m.tolerance, 0.01, 0.5, md.tolerance), bgTolerance: num(m.bgTolerance, 0.01, 0.3, md.bgTolerance), featherPx: num(m.featherPx, 0, 20, md.featherPx), closePx: num(m.closePx, 0, 20, md.closePx) },
    shading: { blurPx: s.blurPx === 'auto' || s.blurPx === undefined ? 'auto' : num(s.blurPx, 1, 30, 3), foldBlurPx: s.foldBlurPx === 'auto' || s.foldBlurPx === undefined ? 'auto' : num(s.foldBlurPx, 1, 60, 12), wrinkle: num(s.wrinkle, 0, 0.03, sd.wrinkle), texture: num(s.texture, 0, 1, sd.texture), lmid: s.lmid === 'auto' || s.lmid === undefined ? 'auto' : num(s.lmid, 0.05, 0.95, 0.5) },
  };
}
